import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import CamperManagementContainer from './CamperManagementContainer';
import './CamperEditPage.css';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { db, functions } from '../../../config/firebase';
import { httpsCallable } from 'firebase/functions';

const CamperEditPage: React.FC = () => {
  const { camperId } = useParams<{ camperId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [par, setPar] = useState<number | null>(null);
  const [parLoading, setParLoading] = useState(false);
  const [parSaving, setParSaving] = useState(false);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | 'current'>('current');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [currentEmail, setCurrentEmail] = useState<string>('');
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [changeEmailLoading, setChangeEmailLoading] = useState(false);
  const [changeEmailError, setChangeEmailError] = useState<string | null>(null);
  const [changeEmailSuccess, setChangeEmailSuccess] = useState<string | null>(null);

  const returnTo = searchParams.get('returnTo');
  const safeReturnPath = returnTo && returnTo.startsWith('/') ? returnTo : '/admin/campers';

  const handleBack = () => {
    console.log('Navigating back to campers list');
    navigate(safeReturnPath);
  };

  const handleSaveSuccess = () => {
    console.log('Save successful, navigating back to campers list');
    navigate(safeReturnPath);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (camperId && camperId !== 'new') {
        setParLoading(true);
        try {
          // Fetch PAR value
          const docSnap = await import('../../../config/firebase').then(({ db }) =>
            import('firebase/firestore').then(({ doc, getDoc }) => getDoc(doc(db, 'primaryCampers', camperId)))
          );
          if (docSnap.exists()) {
            setPar(docSnap.data().par ?? null);
            setCurrentEmail(docSnap.data().email || '');
          }

          // Fetch available archive years
          const archiveSnapshot = await getDocs(collection(db, 'archive'));
          const years = archiveSnapshot.docs
            .map(doc => parseInt(doc.id))
            .filter(year => !isNaN(year))
            .sort((a, b) => b - a); // Sort descending (most recent first)
          setAvailableYears(years);
        } catch (e) {
          setError('Failed to load data');
        } finally {
          setParLoading(false);
        }
      }
    };
    fetchData();
  }, [camperId]);

  const handleParChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? null : parseFloat(e.target.value);
    setPar(value);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    if (camperId && camperId !== 'new') {
      debounceTimeout.current = setTimeout(async () => {
        setParSaving(true);
        try {
          await updateDoc(doc(db, 'primaryCampers', camperId), { par: value });
        } catch (e) {
          setError('Failed to update PAR value');
        } finally {
          setParSaving(false);
        }
      }, 600);
    }
  };

  const handleChangeEmail = async () => {
    if (!camperId || !newEmail.trim()) return;

    setChangeEmailLoading(true);
    setChangeEmailError(null);
    setChangeEmailSuccess(null);

    try {
      const adminChangeEmailFn = httpsCallable(functions, 'adminChangeEmail');
      const result = await adminChangeEmailFn({
        camperId,
        oldEmail: currentEmail,
        newEmail: newEmail.trim()
      });

      const data = result.data as any;
      setChangeEmailSuccess(data.message || 'Email updated successfully');
      setCurrentEmail(newEmail.trim().toLowerCase());
      setNewEmail('');
      setTimeout(() => {
        setShowChangeEmailModal(false);
        setChangeEmailSuccess(null);
      }, 2000);
    } catch (err: any) {
      const message = err?.message || 'Failed to change email';
      setChangeEmailError(message);
    } finally {
      setChangeEmailLoading(false);
    }
  };

  if (!isAdmin) {
    return <div className="error-message">Access denied. Admin privileges required.</div>;
  }

  return (
    <div className="camper-edit-page">
      <div className="edit-page-content">
        <div className="edit-page-header">
          <button onClick={handleBack} className="back-button">
            ← Back to Campers
          </button>
          <h1>{camperId === 'new' ? 'New Camper' : 'Edit Camper'}</h1>
          {camperId !== 'new' && (
            <div style={{ marginTop: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label htmlFor="year-selector" style={{ fontWeight: 600, fontSize: '1.1em' }}>Viewing:</label>
                <select
                  id="year-selector"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value === 'current' ? 'current' : parseInt(e.target.value))}
                  style={{ 
                    fontSize: '1em', 
                    padding: '0.4em 0.8em',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    backgroundColor: selectedYear === 'current' ? 'white' : '#fff3cd'
                  }}
                >
                  <option value="current">Current Year</option>
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year} Archive</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <label htmlFor="par-field" style={{ fontWeight: 600, fontSize: '1.1em' }}>PAR:</label>
                <input
                  id="par-field"
                  type="number"
                  min="0"
                  step="0.01"
                  value={par === null || par === undefined ? '' : par}
                  onChange={handleParChange}
                  disabled={selectedYear !== 'current'}
                  style={{ 
                    width: '120px', 
                    fontSize: '1.1em', 
                    padding: '0.3em',
                    backgroundColor: selectedYear !== 'current' ? '#f0f0f0' : 'white'
                  }}
                />
                <span style={{ fontWeight: 500, color: '#2c3e50' }}>
                  {typeof par === 'number' ? par.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' }) : '$0.00'}
                </span>
                {parSaving && <span style={{ color: '#888', fontSize: '0.95em' }}>Saving...</span>}
              </div>
              {currentEmail && selectedYear === 'current' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 600, fontSize: '1.1em' }}>Email:</span>
                  <span style={{ fontSize: '1em', color: '#333' }}>{currentEmail}</span>
                  <button
                    onClick={() => {
                      setNewEmail('');
                      setChangeEmailError(null);
                      setChangeEmailSuccess(null);
                      setShowChangeEmailModal(true);
                    }}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '0.3rem 0.75rem',
                      fontSize: '0.85em',
                      cursor: 'pointer'
                    }}
                  >
                    Change Email
                  </button>
                </div>
              )}
            </div>
          )}
          {selectedYear !== 'current' && (
            <div style={{ 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '0.75rem 1rem',
              marginTop: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ fontSize: '1.2em' }}>⚠️</span>
              <span style={{ fontWeight: 500, color: '#856404' }}>
                Viewing Archived Data from {selectedYear} - Read Only
              </span>
            </div>
          )}
        </div>
        {error && (
          <div className="error-message">{error}</div>
        )}
        <CamperManagementContainer
          primaryCamperId={camperId === 'new' ? undefined : camperId}
          isEditing={camperId !== 'new'}
          onSave={handleSaveSuccess}
          selectedYear={selectedYear}
        />
      </div>

      {showChangeEmailModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Change Email Address</h3>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: '#666' }}>Current Email</label>
              <input
                type="email"
                value={currentEmail}
                disabled
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#f0f0f0',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: '#666' }}>New Email</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter new email address"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '6px',
              padding: '0.75rem',
              marginBottom: '1rem',
              fontSize: '0.85rem',
              color: '#856404'
            }}>
              This will update the login email and camper record. A notification will be sent to both the old and new email addresses.
            </div>

            {changeEmailError && (
              <div style={{
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '4px',
                padding: '0.75rem',
                marginBottom: '1rem',
                color: '#721c24',
                fontSize: '0.9rem'
              }}>
                {changeEmailError}
              </div>
            )}

            {changeEmailSuccess && (
              <div style={{
                backgroundColor: '#d4edda',
                border: '1px solid #c3e6cb',
                borderRadius: '4px',
                padding: '0.75rem',
                marginBottom: '1rem',
                color: '#155724',
                fontSize: '0.9rem'
              }}>
                {changeEmailSuccess}
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowChangeEmailModal(false)}
                disabled={changeEmailLoading}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleChangeEmail}
                disabled={changeEmailLoading || !newEmail.trim()}
                style={{
                  backgroundColor: changeEmailLoading ? '#999' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: changeEmailLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                {changeEmailLoading ? 'Updating...' : 'Change Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CamperEditPage; 